/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  type ComponentChildren,
  type ComponentProps,
  type Ref,
  type JSX,
  toChildArray
} from 'preact';
import { forwardRef, useRef } from 'preact/compat';

import { BaseRichSelection } from '#PRIVATE_RichSelectionCommon';
import type { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { RadioSetContext } from '#UNSAFE_RadioSet/RadioSetContext';
import { useId } from '#hooks/UNSAFE_useId';
import { RadioItemContext } from '#UNSAFE_RadioItem/RadioItemContext';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import type { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { Size } from '#utils/UNSAFE_size';
import { useRadioFocusManager } from '#hooks/PRIVATE_useRadioFocusManager';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { TabbableModeContext, useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

// Typing should stay in sync with BaseRichSelection. They cannot be imported directly
// because BaseRichSelection is in a PRIVATE directory and will be removed in the production build.
type Props = TestIdProps &
  Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-describedby'> & {
    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];
    /**
     * Set of RichSelectionItems
     */
    children: ComponentChildren;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
    /**
     * Specifies if the set is disabled.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the set is read-only.
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the set requires a selection to be made.
     */
    isRequired?: boolean;
    /**
     * Label text for the set.
     */
    label: string;
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'inside' | 'start';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Specifies the layout of the item cards and media
     */
    layout: 'xl' | 'md' | 'sm';
    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];
    /**
     * Callback invoked each time the user changes selected option.
     */
    onCommit: (detail: ValueUpdateDetail<string | number | undefined>) => void;
    /**
     * Specifies the density of the user assistance presentation.  It can be set to:
     * <ul>
     * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;
    /**
     * Value of the radio option.
     */
    value?: string | number;
  };

/**
 * A rich radio set allows the user to select one option from a set of mutually exclusive options.
 */
export const RichRadioSet = forwardRef(
  (
    {
      children,
      onCommit,
      value,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      ...otherProps
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const baseId = useId();

    const { isDisabled: isFormDisabled, isReadonly: isFormReadonly } = useFormContext();

    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;

    // When in readonly mode, we need to navigate the readonly divs using arrows keys to mimic default browser behavior for radiosets
    const containerRef = useRef<HTMLDivElement>(null);
    const { setFocusIndex, handleArrowKeyPress } = useRadioFocusManager(containerRef, children);

    const { focusProps, isFocused } = useFocusWithin({
      isDisabled
    });

    const { isTabbable } = useTabbableMode();

    return (
      <RadioSetContext.Provider
        value={{
          name: baseId,
          value,
          onCommit,
          setFocusIndex
        }}>
        <TabbableModeContext.Provider
          value={{ isTabbable: isFocused && isReadonly ? false : isTabbable }}>
          <div
            ref={containerRef}
            onKeyDown={isReadonly ? handleArrowKeyPress : undefined}
            {...(isReadonly ? focusProps : {})}>
            <BaseRichSelection
              {...otherProps}
              isDisabled={propIsDisabled}
              isReadonly={propIsReadonly}
              baseId={baseId}
              ref={ref}
              selection="single"
              value={value}>
              {toChildArray(children).map((child, index) => (
                // index is needed for keyboard navigation with radio items
                <RadioItemContext.Provider value={{ index }}>{child}</RadioItemContext.Provider>
              ))}
            </BaseRichSelection>
          </div>
        </TabbableModeContext.Provider>
      </RadioSetContext.Provider>
    );
  }
);
